import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import useUser from '@auth/useUser';
import { startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';

/**
 * The Error404Page component renders a custom 404 error page.
 */
function Error404Page() {
  const { isGuest } = useUser();
  const navigate = useNavigate();
  const destination = isGuest ? '/sign-in' : '/';
  const label = isGuest ? 'Back to sign-in' : 'Back to Home';

  const handleNavigate = () => {
    startTransition(() => {
      navigate(destination);
    });
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center p-16">
      <div className="w-full max-w-2xl text-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
          className="flex justify-center"
        >
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            <FuseSvgIcon size={48}>heroicons-outline:document-search</FuseSvgIcon>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}>
          <Typography
            variant="h1"
            className="mt-24 text-center text-7xl font-extrabold leading-none tracking-tight md:text-8xl"
            color="text.primary"
          >
            404
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.3 } }}>
          <Typography
            variant="h5"
            className="mt-16 text-center text-2xl font-semibold tracking-tight md:text-3xl"
            color="text.primary"
          >
            Page not found
          </Typography>
          <Typography
            className="mt-8 text-center text-base font-medium md:text-lg"
            color="text.secondary"
          >
            The page you requested doesn't exist or may have been moved.
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.4 } }}
        >
          <Button className="mt-32" variant="contained" color="secondary" onClick={handleNavigate}>
            {label}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

export default Error404Page;
